/** Shared utilities for {@link AdminRow}, {@link AdminCard} and {@link AdminsDataTable}. */
import { Mail, Phone, type LucideIcon } from 'lucide-react';
import type { InterfaceElementResponseDto } from '@/types/interface-elements.types';
import type { GetUsersParams, PrimaryContactKind } from '@/types/user.types';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import { resolveUserContacts } from '@/utils/user-contact-utils';
import type { AdminFilters } from './admin-configs';

/** Primary contact icon + value, with optional secondary shown under it. */
export interface AdminContactDisplay {
  icon: LucideIcon;
  value: string;
  secondaryValue: string | null;
}

/** Phone vs email from `primaryContactKind`; `N/R` when empty. */
export function deriveAdminContact(admin: {
  email?: string | null;
  phone?: string | null;
  primaryContactKind?: PrimaryContactKind | null;
}): AdminContactDisplay {
  const resolved = resolveUserContacts(admin.primaryContactKind, admin.email, admin.phone);
  return {
    icon: resolved.kind === 'phone' ? Phone : Mail,
    value: resolved.primary?.value ?? 'N/R',
    secondaryValue: resolved.secondary?.value ?? null,
  };
}

/**
 * Pure helper for the canEdit / canDelete memos shared between {@link AdminCard} and {@link AdminRow}.
 * Returns whether a given permission subtype is granted for any of the admin's roles.
 */
export function resolveAdminPermission(
  userRoles: Array<{ role: { code: string } }> | undefined,
  interfaceElements: InterfaceElementResponseDto[],
  subtype: 'edit-role' | 'delete-role',
  defaultRole = 'admin',
): boolean {
  const codes = userRoles?.map((ur) => ur.role.code) ?? [];
  const codesToCheck = codes.length > 0 ? codes : [defaultRole];
  return interfaceElements.some((el) => {
    const targetRole = (el.config as { subtype?: string; targetRole?: string } | undefined)
      ?.targetRole;
    return (
      el.elementType === 'action' &&
      (el.config as { subtype?: string } | undefined)?.subtype === subtype &&
      targetRole != null &&
      codesToCheck.includes(targetRole)
    );
  });
}

/** Period preset or custom dates → `dateFrom` / `dateTo`. */
export function resolveAdminListDateRange(
  filters: Pick<AdminFilters, 'dateRange' | 'customDateFrom' | 'customDateTo'>,
) {
  return resolveListDateRange(filters);
}

/** Map UI filters to admin list query params. */
function toAdminFilterParams(
  filters: AdminFilters,
  roleCodes?: string[],
): Omit<GetUsersParams, 'page' | 'pageSize'> {
  const { dateFrom, dateTo } = resolveAdminListDateRange(filters);
  return {
    search: filters.searchQuery?.trim() || undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    establishmentId: filters.establishmentId,
    programIds: filters.programIds?.length ? filters.programIds : undefined,
    departmentCodes: filters.departmentCodes?.length ? filters.departmentCodes : undefined,
    roleCodes: roleCodes && roleCodes.length > 0 ? roleCodes : undefined,
    dateFrom,
    dateTo,
  };
}

/** Map UI filters + pagination to admin list query params. */
export function buildAdminListQueryParams(
  filters: AdminFilters,
  options: { page?: number; pageSize?: number; roleCodes?: string[] } = {},
): GetUsersParams {
  return {
    ...toAdminFilterParams(filters, options.roleCodes),
    page: options.page,
    pageSize: options.pageSize,
  };
}

/** Admin export params (same filters as the list, no pagination). */
export function buildAdminExportQueryParams(
  filters: AdminFilters,
  roleCodes?: string[],
): Omit<GetUsersParams, 'page' | 'pageSize'> {
  return toAdminFilterParams(filters, roleCodes);
}

/** True when any admin chip / date / search filter is set. */
export function hasAdminActiveFilters(filters: AdminFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.establishmentId !== undefined ||
    (filters.programIds?.length ?? 0) > 0 ||
    (filters.departmentCodes?.length ?? 0) > 0 ||
    !!filters.searchQuery?.trim() ||
    filters.dateRange !== 'all' ||
    filters.customDateFrom !== undefined ||
    filters.customDateTo !== undefined
  );
}
